/**
 * Phase 101gc (Feb 2026) — Sélecteur de distributeur pour le module Tournée.
 *
 * Écran d'entrée quand un livreur (role=driver) se connecte, ou quand un
 * admin/operator clique « Ma tournée » depuis la sidebar.
 *
 * Route : `/tournee`
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronRight, Loader2, Search, AlertTriangle, LogOut, RefreshCw, Smartphone, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useTourneePWA } from "../lib/useTourneePWA";

export default function FieldTourList() {
  const { user, logout } = useAuth();
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  // Phase 101ea.12 : PWA installable pour BOF (icône home screen + fullscreen)
  const { canInstall, isInstalled, triggerInstall } = useTourneePWA();
  const [installBannerDismissed, setInstallBannerDismissed] = useState(() => {
    try { return localStorage.getItem("tournee-pwa-banner-dismissed") === "1"; }
    catch { return false; }
  });
  const dismissBanner = () => {
    setInstallBannerDismissed(true);
    try { localStorage.setItem("tournee-pwa-banner-dismissed", "1"); } catch {}
  };
  const doInstall = async () => {
    const r = await triggerInstall();
    if (r?.outcome === "accepted") {
      dismissBanner();
    }
  };

  const isDriver = user?.role === "driver";

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/field-tour/distributors");
      setDistributors(r.data.distributors || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return distributors;
    return distributors.filter((d) =>
      (d.name || "").toLowerCase().includes(s) ||
      (d.city || "").toLowerCase().includes(s) ||
      (d.code || "").toLowerCase().includes(s)
    );
  }, [distributors, q]);

  // Phase 101fk.6 — Totaux du jour (comme la page d'accueil Moma)
  const totals = useMemo(() => {
    const t = { sales: 0, revenue: 0, barquettes: 0, dlc_soon: 0 };
    for (const d of distributors) {
      t.sales += d.sales_today || 0;
      t.revenue += d.revenue_today || 0;
      t.barquettes += d.total_barquettes || 0;
      t.dlc_soon += d.dlc_soon_barquettes || 0;
    }
    return t;
  }, [distributors]);

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="field-tour-list-page">
      <header className="bg-gradient-to-br from-[#1F4E3D] to-[#173B2E] text-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#A8C7B8]">Tournée terrain</div>
              <div className="text-xl sm:text-2xl font-heading mt-0.5">Choisir une machine</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={load}
                disabled={loading}
                className="text-white hover:bg-[#173B2E] w-10 h-10"
                data-testid="field-tour-list-refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
              </Button>
              {isDriver && (
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white hover:bg-[#173B2E] gap-2"
                  data-testid="field-tour-list-logout"
                >
                  <LogOut className="w-4 h-4" /> Déconnexion
                </Button>
              )}
            </div>
          </div>
          {user?.name && (
            <div className="text-xs text-[#A8C7B8] mt-1">
              Bonjour {user.name}, sélectionne la machine à visiter.
            </div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {/* Phase 101ea.12 — Bannière d'installation PWA (mobile Chrome/Edge) */}
        {canInstall && !isInstalled && !installBannerDismissed && (
          <div
            className="bg-gradient-to-br from-[#1F4E3D] to-[#173B2E] text-white rounded-lg p-4 flex items-start gap-3 shadow-md"
            data-testid="pwa-install-banner"
          >
            <div className="bg-white/15 rounded-full p-2 shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm mb-1">
                Installer « Tournée » sur ton téléphone
              </div>
              <div className="text-xs text-[#A8C7B8] mb-3 leading-relaxed">
                Ajoute l'appli à ton écran d'accueil : un clic pour ouvrir, plus besoin de retaper ton mot de passe à chaque fois.
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={doInstall}
                  className="bg-white text-[#1F4E3D] hover:bg-[#F5F5F0] h-8"
                  data-testid="pwa-install-btn"
                >
                  Installer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dismissBanner}
                  className="text-white hover:bg-white/10 h-8"
                  data-testid="pwa-install-dismiss"
                >
                  Plus tard
                </Button>
              </div>
            </div>
            <button
              onClick={dismissBanner}
              className="text-white/70 hover:text-white shrink-0"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Recherche */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A6A3]" />
          <Input
            type="search"
            placeholder="Chercher par nom, ville ou code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
            data-testid="field-tour-search"
          />
        </div>

        {/* Phase 101fk.6 — Totaux du jour (aperçu type Moma) */}
        {!loading && distributors.length > 0 && (
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3 grid grid-cols-4 gap-2 text-center" data-testid="field-tour-totals">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">Ventes</div>
              <div className="text-lg font-heading text-[#1F4E3D] font-semibold">{totals.sales}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">CA</div>
              <div className="text-lg font-heading text-[#1F4E3D] font-semibold">{totals.revenue.toFixed(0)}<span className="text-xs ml-0.5">CHF</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">Barq.</div>
              <div className="text-lg font-heading text-[#1F4E3D] font-semibold">{totals.barquettes}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">DLC ↕</div>
              <div className={`text-lg font-heading font-semibold ${totals.dlc_soon > 0 ? "text-[#8C6B2F]" : "text-[#1F4E3D]"}`}>{totals.dlc_soon}</div>
            </div>
          </div>
        )}

        {loading && distributors.length === 0 && (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1F4E3D]" /></div>
        )}

        {error && (
          <div className="bg-[#FBE5E0] border border-[#E8C7BD] rounded-lg p-4 text-sm text-[#8C3A2A] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Liste */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-sm text-[#A3A6A3]">
            {q ? "Aucune machine ne correspond à ta recherche." : "Aucun distributeur disponible."}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="space-y-2" data-testid="field-tour-dist-list">
            {filtered.map((d) => {
              const dlcSoon = d.dlc_soon_barquettes || 0;
              const totalBarq = d.total_barquettes || 0;
              // Phase 101gc.6 — Pastille état pour aider les livreurs à
              // choisir quelle machine visiter en priorité.
              let statusPill = null;
              if (dlcSoon > 0) {
                statusPill = { label: `${dlcSoon} DLC courte`, bg: "#FBEEDB", text: "#8C6B2F", icon: "⏰" };
              } else if (totalBarq === 0) {
                statusPill = { label: "Vide", bg: "#FBE5E0", text: "#8C3A2A", icon: "!" };
              } else if (totalBarq > 0 && totalBarq < 5) {
                statusPill = { label: "À réappro", bg: "#FBE5E0", text: "#8C3A2A", icon: "↑" };
              } else {
                statusPill = { label: "OK", bg: "#E8F1EA", text: "#1F4E3D", icon: "✓" };
              }
              return (
                <Link
                  key={d.id}
                  to={`/tournee/${d.id}`}
                  className="block bg-white border-2 border-[#E5E5E0] rounded-lg p-3 hover:border-[#1F4E3D] hover:shadow-sm transition-all active:scale-[0.99]"
                  data-testid={`field-tour-dist-${d.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#1F4E3D] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1A1C1A]">{d.name}</div>
                      <div className="text-xs text-[#575A57] flex gap-2 flex-wrap">
                        {d.code && <span className="font-mono">{d.code}</span>}
                        {d.city && <span>· {d.city}</span>}
                        {d.total_slots > 0 && <span>· {d.total_slots} slots</span>}
                      </div>
                      {/* Phase 101fk.6 — Ventes du jour (comme Moma) */}
                      <div className="text-[11px] text-[#1F4E3D] font-medium mt-0.5 flex gap-2 flex-wrap">
                        <span>🛒 {d.sales_today || 0} vente{(d.sales_today || 0) > 1 ? "s" : ""}</span>
                        {(d.revenue_today || 0) > 0 && (
                          <span className="text-[#575A57]">· CA {Number(d.revenue_today).toFixed(2)} CHF</span>
                        )}
                      </div>
                    </div>
                    {/* Pastille état + compteur barq. bien visible */}
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: statusPill.bg, color: statusPill.text }}
                      >
                        <span>{statusPill.icon}</span>
                        <span>{statusPill.label}</span>
                      </div>
                      <div className="text-[10px] text-[#A3A6A3]">
                        {totalBarq} barq.
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A3A6A3] shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
